import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { logger } from "@/utils/logger";
import { beginOAuthRoundTrip, OAUTH_STATE_PARAM } from "@/utils/authUtils";
import { toast } from "sonner";

/** Official Google "G" mark — lucide-react carries no brand icons. */
function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

interface GoogleSignInButtonProps {
  /** App path to land on once the session is established. */
  returnUrl?: string;
  className?: string;
}

/**
 * Starts Supabase's Google OAuth flow. The browser leaves the app entirely, so
 * this component never sees a session — it comes back to /auth/callback.
 *
 * Supabase links the Google identity into an existing account automatically when
 * that account's address matches Google's (verified) email, so an existing
 * password user keeps the same auth.users.id — and therefore the same profile,
 * org roles and assignments.
 */
export function GoogleSignInButton({
  returnUrl = "/buildings",
  className,
}: GoogleSignInButtonProps) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);

  // Coming back from Google's consent screen can restore this page from bfcache,
  // which keeps React state — so the deliberate "stay disabled while navigating
  // away" latch would leave the button dead until a manual reload. Release it.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setLoading(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const handleClick = async () => {
    setLoading(true);
    try {
      // What ties the redirect that comes back to this click — the implicit flow binds
      // nothing by itself, see beginOAuthRoundTrip. No state, no sign-in: a round trip the
      // callback cannot verify is one it refuses, so there is no point starting it.
      const state = beginOAuthRoundTrip();
      if (!state) {
        logger.error(
          "auth",
          "google-oauth-state-unavailable",
          "Session storage refused the OAuth state, so the round trip cannot be verified"
        );
        toast.error(t.auth.googleSignInFailed);
        setLoading(false);
        return;
      }

      const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent(returnUrl)}&${OAUTH_STATE_PARAM}=${state}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      });
      if (error) {
        logger.error("auth", "google-oauth-start-failed", error.message, error);
        toast.error(t.auth.googleSignInFailed);
        setLoading(false);
      }
      // On success the browser is navigating away — keep the button disabled.
    } catch (error) {
      logger.error("auth", "google-oauth-start-error", "Unexpected error", error as Error);
      toast.error(t.auth.googleSignInFailed);
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      className={className ?? "w-full"}
      disabled={loading}
      onClick={handleClick}
    >
      <GoogleLogo />
      {loading ? t.auth.signingIn : t.auth.continueWithGoogle}
    </Button>
  );
}
